"use client";

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { getCsrfToken, signOut, useSession } from 'next-auth/react';
import { normalizeAuthCallbackPath, normalizeAuthProduct } from '/lib/auth/productAuth';
import styles from './ProductAuthControl.module.css';

export default function ProductAuthControl({ product, callbackUrl = '/', compact = false, navbar = false }) {
  const { data: session, status } = useSession();
  const [authPending, setAuthPending] = useState(false);
  const [authError, setAuthError] = useState('');
  const [points, setPoints] = useState(null);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileTriggerRef = useRef(null);
  const normalizedProduct = normalizeAuthProduct(product);
  const safeCallbackUrl = normalizeAuthCallbackPath(callbackUrl);
  const user = session?.user || null;

  useEffect(() => {
    if (!user || !navbar || normalizedProduct !== 'shuffledb') return;
    fetch('/api/profile').then((response) => response.ok ? response.json() : null)
      .then((result) => setPoints(result?.user?.active_points ?? null)).catch(() => {});
    fetch('/api/notifications').then((response) => response.ok ? response.json() : null)
      .then((result) => setUnreadNotifications(result?.unreadCount || 0)).catch(() => {});
  }, [navbar, normalizedProduct, user]);

  // Product sign-in normally completes in a separate tab. Once the shared
  // SessionProvider observes that login, release the pending state in the
  // original product tab without requiring a reload.
  useEffect(() => {
    if (status !== 'authenticated') return;
    setAuthPending(false);
    setAuthError('');
  }, [status]);

  useEffect(() => {
    if (!profileMenuOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!profileTriggerRef.current?.contains(event.target)) setProfileMenuOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setProfileMenuOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [profileMenuOpen]);

  useEffect(() => {
    if (!user) setProfileMenuOpen(false);
  }, [user]);

  async function handleSignIn() {
    if (authPending) return;
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const returnPath = normalizeAuthCallbackPath(currentPath, safeCallbackUrl);
    // PostDat setup needs to return to the exact workspace route so its
    // connection state can be reloaded immediately after authentication.
    // Keep the established popup flow for the other products.
    const authWindow = normalizedProduct === 'postdat' ? null : window.open('', '_blank');
    const absoluteCallbackUrl = new URL(returnPath, window.location.origin).toString();
    setAuthPending(true);
    setAuthError('');
    try {
      if (authWindow) {
        authWindow.document.title = 'TrackDat Google sign-in';
        authWindow.document.body.textContent = 'Opening Google sign-in…';
      }
      const response = await fetch('/api/auth/product-origin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ product: normalizedProduct, callbackUrl: returnPath }),
      });
      if (!response.ok) throw new Error('Could not prepare product sign-in.');

      const csrfToken = await getCsrfToken();
      if (!csrfToken) throw new Error('Could not prepare the sign-in security token.');
      const signInResponse = await fetch('/api/auth/signin/google', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        credentials: 'same-origin',
        body: new URLSearchParams({
          csrfToken,
          callbackUrl: absoluteCallbackUrl,
          json: 'true',
        }),
      });
      const signInResult = await signInResponse.json();
      if (!signInResponse.ok || !signInResult?.url) {
        throw new Error('Google did not return a sign-in URL.');
      }

      if (authWindow) {
        authWindow.location.replace(signInResult.url);
      } else {
        window.location.assign(signInResult.url);
      }
    } catch (error) {
      authWindow?.close();
      console.error('[Auth] Product sign-in failed', error);
      setAuthError('Sign in could not be started. Try again.');
      setAuthPending(false);
    }
  }

  async function handleSignOut() {
    if (authPending) return;
    setAuthPending(true);
    setAuthError('');
    try {
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const returnPath = normalizeAuthCallbackPath(currentPath, safeCallbackUrl);
      await signOut({ callbackUrl: new URL(returnPath, window.location.origin).toString() });
    } catch (error) {
      console.error('[Auth] Product sign-out failed', error);
      setAuthError('Sign out could not be completed. Try again.');
      setAuthPending(false);
    }
  }

  const loading = status === 'loading';
  const displayName = user?.name?.trim() || user?.email?.split('@')[0] || 'ShuffleDB member';
  const productAccountLabel = normalizedProduct === 'capdat' ? 'CapDat account' : normalizedProduct === 'postdat' ? 'PostDat account' : normalizedProduct === 'editdat' ? 'EditDat account' : normalizedProduct === 'trackdat' ? 'TrackDat account' : 'ShuffleDB account';

  return (
    <div className={`${styles.control} ${compact ? styles.compact : ''} ${navbar ? styles.navbar : ''}`}>
      <div ref={profileTriggerRef} className={`${styles.identity} ${user && navbar ? styles.profileTrigger : ''}`}>
        {user && navbar ? (
          <button
            type="button"
            className={styles.profileButton}
            aria-label={`Open ${displayName}'s profile menu`}
            aria-haspopup="menu"
            aria-expanded={profileMenuOpen}
            onClick={() => setProfileMenuOpen((open) => !open)}
          >
            {user.image ? (
              // OAuth avatars are remote, user-specific URLs that are not known at build time.
              // eslint-disable-next-line @next/next/no-img-element
              <img className={styles.avatar} src={user.image} alt="" referrerPolicy="no-referrer" />
            ) : (
              <span className={styles.avatarFallback} aria-hidden="true">
                {displayName.slice(0, 1).toUpperCase()}
              </span>
            )}
          </button>
        ) : user?.image ? (
          // OAuth avatars are remote, user-specific URLs that are not known at build time.
          // eslint-disable-next-line @next/next/no-img-element
          <img className={styles.avatar} src={user.image} alt="" referrerPolicy="no-referrer" />
        ) : (
          <span className={styles.avatarFallback} aria-hidden="true">•</span>
        )}
        <span className={`${styles.identityCopy} ${navbar ? styles.navbarIdentityCopy : ''}`}>
          <strong>{user ? displayName : productAccountLabel}</strong>
          <small>{user?.email || 'One login across ShuffleDB tools'}</small>
        </span>
        {user && navbar && (
          <div
            className={`${styles.profileMenu} ${profileMenuOpen ? styles.profileMenuOpen : ''}`}
            role="menu"
            onClick={() => setProfileMenuOpen(false)}
          >
            <Link role="menuitem" href="/Profile">View Profile</Link>
            {['trackdat', 'capdat'].includes(normalizedProduct) && (
              <Link
                role="menuitem"
                href="/CapDat#bridge"
                target={normalizedProduct === 'trackdat' ? '_blank' : undefined}
                rel={normalizedProduct === 'trackdat' ? 'noopener noreferrer' : undefined}
              >
                Pair CapDat
              </Link>
            )}
            {normalizedProduct === 'shuffledb' && (
              <>
                <Link role="menuitem" href="/Contributors">Contributors</Link>
                <Link role="menuitem" href="/Leaderboard">Leaderboard</Link>
                <Link role="menuitem" href="/Missions">My Missions</Link>
                <Link role="menuitem" href="/Profile#notifications">Notifications{unreadNotifications ? ` (${unreadNotifications})` : ''}</Link>
                <Link role="menuitem" href="/Support">Help &amp; Support</Link>
                {user.isAdmin === true && <Link role="menuitem" href="/UserManagement">User Management</Link>}
                {user.isAdmin === true && <Link role="menuitem" href="/PointsAdmin">Points &amp; Ranks</Link>}
                {user.isAdmin === true && <Link role="menuitem" href="/EmailOutboxAdmin">Email Delivery</Link>}
                {user.isAdmin === true && <Link role="menuitem" href="/MissionReview">Mission Review</Link>}
                {user.isAdmin === true && <Link role="menuitem" href="/ContentAdmin">Content Admin</Link>}
                {user.isAdmin === true && <Link role="menuitem" href="/MediaRightsAdmin">Media Rights</Link>}
                {user.isAdmin === true && <Link role="menuitem" href="/SupportAdmin">Support Queue</Link>}
                {user.isAdmin === true && <Link role="menuitem" href="/AdminPanel">Admin Tools</Link>}
                <span>{points == null ? 'Points loading…' : `${points} active points`}</span>
              </>
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        className={styles.authButton}
        disabled={loading || authPending}
        aria-busy={loading || authPending}
        onClick={user ? handleSignOut : handleSignIn}
        data-trackdat-google-auth-popup={!user ? 'true' : undefined}
      >
        {loading || authPending ? 'Loading…' : user ? 'Sign out' : 'Sign in with Google'}
      </button>
      {!user && normalizedProduct === 'shuffledb' && <Link className={styles.emailLink} href="/auth/email">Use email</Link>}
      {authError && <span className={styles.error}>{authError}</span>}
    </div>
  );
}
